import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, FileText, Users, Clock, TrendingUp, Plus } from 'lucide-react';
import { useBatchStats, useRecentBatches } from '@/hooks/useBatchHistory';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  trend?: {
    value: string;
    direction: 'up' | 'down' | 'neutral';
  };
  error?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon: Icon, trend, error }) => {
  if (error) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-6 w-6 text-red-500" />
          </div>
          <div className="ml-4">
            <h3 className="text-sm font-medium text-slate-900">{title}</h3>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <Icon className="h-6 w-6 text-slate-500" />
        </div>
        <div className="ml-4 flex-1">
          <h3 className="text-sm font-medium text-slate-900">{title}</h3>
          <div className="flex items-baseline">
            <p className="text-2xl font-semibold text-slate-900">{value}</p>
            {trend && (
              <span className={`ml-2 text-sm ${
                trend.direction === 'up' ? 'text-green-600' :
                trend.direction === 'down' ? 'text-red-600' : 'text-slate-500'
              }`}>
                {trend.value}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
    <AlertTriangle className="h-8 w-8 text-slate-400 mx-auto mb-3" />
    <p className="text-slate-600">{message}</p>
  </div>
);

export const DashboardPage: React.FC = () => {
  const { stats, loading: statsLoading } = useBatchStats();
  const { recentBatches, loading: batchesLoading } = useRecentBatches(5);
  
  const loading = statsLoading || batchesLoading;

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="h-4 bg-slate-200 rounded mb-2"></div>
                <div className="h-8 bg-slate-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="space-y-3">
        <EmptyState message="Unable to load dashboard data. Please check your connection and try again." />
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          title="Total Batches"
          value={stats.total}
          icon={FileText}
        />
        <StatCard
          title="Completed"
          value={stats.completed}
          icon={TrendingUp}
          trend={{
            value: stats.total > 0 ? `${Math.round((stats.completed / stats.total) * 100)}%` : '0%',
            direction: 'neutral'
          }}
        />
        <StatCard
          title="Processing"
          value={stats.processing}
          icon={Clock}
        />
        <StatCard
          title="Records Processed"
          value={stats.totalRecordsProcessed.toLocaleString()}
          icon={Users}
          trend={{
            value: `${Math.round(stats.successfulRecords / Math.max(stats.totalRecordsProcessed, 1) * 100)}% success`,
            direction: 'up'
          }}
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-medium text-slate-900">Recent Batches</h2>
          </div>
          <div className="p-4">
            {recentBatches.length === 0 ? (
              <EmptyState message="No batch history yet. Create your first batch to get started." />
            ) : (
              <div className="space-y-3">
                {recentBatches.map((batch) => (
                  <div key={batch.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full ${
                        batch.status === 'completed' ? 'bg-green-500' : 
                        batch.status === 'failed' ? 'bg-red-500' : 
                        'bg-blue-500'
                      }`} />
                      <div>
                        <div className="text-sm font-medium text-slate-900">{batch.filename}</div>
                        <div className="text-xs text-slate-500">
                          {batch.operation} • {new Date(batch.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-slate-900">{batch.successfulRecords}/{batch.totalRecords}</div>
                      <div className="text-xs text-slate-500">{batch.status}</div>
                    </div>
                  </div>
                ))}
                <Link 
                  to="/admin/batches"
                  className="text-sm text-slate-600 hover:text-blue-800 block text-center mt-3"
                >
                  View all batches →
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-medium text-slate-900">System Status</h2>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Admin Portal</span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                  Online
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Authentication</span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                  Active
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Batch Processing</span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                  Available
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Storage</span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                  Connected
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <h2 className="text-lg font-medium text-slate-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link 
            to="/admin/batches/new"
            className="flex items-center justify-center px-4 py-3 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Create New Batch</span>
          </Link>
          <Link 
            to="/admin/batches"
            className="flex items-center justify-center px-4 py-3 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <FileText className="h-5 w-5 mr-2 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">View All Batches</span>
          </Link>
          <Link 
            to="/admin/search"
            className="flex items-center justify-center px-4 py-3 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <TrendingUp className="h-5 w-5 mr-2 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Individual Search</span>
          </Link>
        </div>
      </div>
    </div>
  );
};
